import {
  type BackgroundRender,
  type BannerRender,
  type CharacterRender,
  type FrameRender,
  type HatRender,
  type TrailRender,
  getItem,
} from "../../../shared/shop-catalog";
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { MAX_NAME_LENGTH } from "../../utils/name";
import {
  drawSceneBackground,
  fillRoundedRect,
  strokeRoundedRect,
  truncateToWidth,
} from "../../utils/draw";
import type { Renderer } from "../Renderer";
import { DAY_PALETTE, NIGHT_PALETTE } from "../entities/Background";
import { drawBannerBg } from "../entities/BannerAccessory";
import { dominantColor } from "../entities/CustomArt";
import type { LeaderboardEntry } from "../systems/Leaderboard";
import { drawCharacterSprite } from "../entities/CharacterSprite";
import { drawFrameRow } from "../entities/FrameAccessory";
import { drawHatBack, drawHatFront } from "../entities/HatAccessory";
import { drawTrailIcon } from "../entities/TrailIcon";
import { backButton } from "../systems/Buttons";
import { ClanScene } from "./ClanScene";
import { MenuScene } from "./MenuScene";
import type { GameContext, Scene } from "./Scene";

const ROW_HEIGHT = 24;
const HEADER_OFFSET = 50;

// Hand-drawn (CUSTOM) renders for the local player's own row. They live only on
// this device, so they can't be shown for anyone else — but the player should
// see their own drawings here. A slot is null unless a custom design is equipped.
interface CustomOverrides {
  char: CharacterRender | null;
  hat: HatRender | null;
  trail: TrailRender | null;
  frame: FrameRender | null;
  banner: BannerRender | null;
  bg: BackgroundRender | null;
}

const NO_OVERRIDES: CustomOverrides = {
  char: null,
  hat: null,
  trail: null,
  frame: null,
  banner: null,
  bg: null,
};

export class LeaderboardScene implements Scene {
  private scrollY = 0;
  private maxScroll = 0;
  private dragLastY: number | null = null;
  // Tap tracking: a pointerdown inside the list that doesn't drag is a tap, used
  // to open a player's clan when the tap lands on their clan tag.
  private tapStart: { x: number; y: number } | null = null;
  private cardX = 0;
  private cardY = 0;
  private cardW = 0;
  private cardH = 0;
  private listTop = 0;
  private listH = 0;

  constructor(
    private readonly ctxMgr: GameContext,
    private readonly renderer: Renderer,
  ) {}

  enter(): void {
    this.ctxMgr.buttons.set([backButton(() => this.ctxMgr.goTo(new MenuScene(this.ctxMgr)))]);

    this.cardW = 320;
    this.cardH = VIRTUAL_HEIGHT - 160;
    this.cardX = (VIRTUAL_WIDTH - this.cardW) / 2;
    this.cardY = 90;
    this.listTop = this.cardY + HEADER_OFFSET;
    this.listH = this.cardH - HEADER_OFFSET - 8;

    this.recomputeScrollBounds();
    this.scrollY = 0;
    void this.ctxMgr.leaderboard.refresh().then(() => this.recomputeScrollBounds());

    this.renderer.canvas.addEventListener("wheel", this.onWheel, { passive: false });
    this.renderer.canvas.addEventListener("pointerdown", this.onDragStart);
    window.addEventListener("pointermove", this.onDragMove);
    window.addEventListener("pointerup", this.onDragEnd);
  }

  exit(): void {
    this.ctxMgr.buttons.clear();
    this.renderer.canvas.removeEventListener("wheel", this.onWheel);
    this.renderer.canvas.removeEventListener("pointerdown", this.onDragStart);
    window.removeEventListener("pointermove", this.onDragMove);
    window.removeEventListener("pointerup", this.onDragEnd);
  }

  onFlap(): void {
    // Intentionally a no-op — tapping the list shouldn't exit the scene
    // (it would break touch-drag scrolling). Use the back button instead.
  }

  update(_dt: number): void {
    // static
  }

  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.scrollY = clamp(this.scrollY + e.deltaY, 0, this.maxScroll);
  };

  private readonly onDragStart = (e: PointerEvent): void => {
    const { x, y } = this.renderer.clientToCanvas(e.clientX, e.clientY);
    if (this.isInsideList(x, y)) {
      this.dragLastY = e.clientY;
      this.tapStart = { x, y };
    }
  };

  private readonly onDragMove = (e: PointerEvent): void => {
    if (this.dragLastY === null) return;
    const dyClient = e.clientY - this.dragLastY;
    this.dragLastY = e.clientY;
    const rect = this.renderer.canvas.getBoundingClientRect();
    const dyCanvas = (dyClient / rect.height) * VIRTUAL_HEIGHT;
    this.scrollY = clamp(this.scrollY - dyCanvas, 0, this.maxScroll);
    // Any real movement cancels the pending tap (it's a scroll drag).
    if (this.tapStart && Math.abs(dyCanvas) > 0.5) this.tapStart = null;
  };

  private readonly onDragEnd = (): void => {
    this.dragLastY = null;
    if (this.tapStart) {
      const id = this.clanIdAt(this.tapStart.x, this.tapStart.y);
      this.tapStart = null;
      if (id != null) this.ctxMgr.goTo(new ClanScene(this.ctxMgr, id));
    }
  };

  // The clan id whose tag is at (x,y) in the scrolled list, or null. Mirrors the
  // tag geometry drawn in render().
  private clanIdAt(x: number, y: number): number | null {
    if (!this.isInsideList(x, y)) return null;
    const entries = this.ctxMgr.leaderboard.all();
    const i = Math.floor((y - this.listTop + this.scrollY) / ROW_HEIGHT);
    const entry = entries[i];
    if (!entry?.clan || entry.clan_id == null) return null;
    const ctx = this.renderer.ctx;
    ctx.font = "13px ui-monospace, monospace";
    const nameText = truncate(entry.name, MAX_NAME_LENGTH);
    const clanX = this.cardX + 74 + ctx.measureText(nameText).width + 8;
    const maxRight = this.cardX + this.cardW - 86;
    if (clanX >= maxRight) return null;
    ctx.font = "10px ui-monospace, monospace";
    const tagW = ctx.measureText(truncateToWidth(ctx, entry.clan, maxRight - clanX)).width;
    return x >= clanX - 3 && x <= clanX + tagW + 3 ? entry.clan_id : null;
  }

  /** The local player's equipped custom designs (null per slot if none). */
  private myCustomOverrides(): CustomOverrides {
    const shop = this.ctxMgr.shop;
    const c = shop.custom;
    return {
      char: c.isEquipped("character") ? shop.getEquippedCharacterRender() : null,
      hat: c.isEquipped("hat") ? shop.getEquippedHatRender() : null,
      trail: c.isEquipped("trail") ? shop.getEquippedTrailRender() : null,
      frame: c.isEquipped("frame") ? shop.getEquippedFrameRender() : null,
      banner: c.isEquipped("banner") ? shop.getEquippedBannerRender() : null,
      bg: c.isEquipped("background") ? shop.getEquippedBackgroundRender(false) : null,
    };
  }

  private recomputeScrollBounds(): void {
    const total = this.ctxMgr.leaderboard.all().length;
    const contentH = total * ROW_HEIGHT;
    this.maxScroll = Math.max(0, contentH - this.listH);
    if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;
  }

  private isInsideList(x: number, y: number): boolean {
    return (
      x >= this.cardX &&
      x <= this.cardX + this.cardW &&
      y >= this.listTop &&
      y <= this.listTop + this.listH
    );
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    ctx.fillStyle = "#f5e9c8";
    ctx.font = "bold 24px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("LEADERBOARD", VIRTUAL_WIDTH / 2, 60);

    const entries = this.ctxMgr.leaderboard.all();

    ctx.fillStyle = "#f5e9c8";
    fillRoundedRect(ctx, this.cardX, this.cardY, this.cardW, this.cardH, 12);
    ctx.strokeStyle = "#7a5e2a";
    ctx.lineWidth = 3;
    strokeRoundedRect(ctx, this.cardX, this.cardY, this.cardW, this.cardH, 12);

    ctx.fillStyle = "#7a5e2a";
    ctx.font = "bold 11px ui-monospace, monospace";
    ctx.textAlign = "left";
    ctx.fillText("#  NAME", this.cardX + 16, this.cardY + 30);
    ctx.textAlign = "right";
    ctx.fillText("PIPES  FLIES", this.cardX + this.cardW - 16, this.cardY + 30);

    if (entries.length === 0) {
      ctx.fillStyle = "#7a5e2a";
      ctx.font = "14px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("No runs yet!", VIRTUAL_WIDTH / 2, this.cardY + this.cardH / 2 - 8);
      ctx.fillText("Play to set your record.", VIRTUAL_WIDTH / 2, this.cardY + this.cardH / 2 + 12);
    } else {
      ctx.save();
      // Clip to the list area so scrolled rows can't bleed past the card edges
      ctx.beginPath();
      ctx.rect(this.cardX + 4, this.listTop, this.cardW - 8, this.listH);
      ctx.clip();

      ctx.font = "13px ui-monospace, monospace";
      const myName = this.ctxMgr.settings.playerName;
      const firstVisible = Math.max(0, Math.floor(this.scrollY / ROW_HEIGHT) - 1);
      const lastVisible = Math.min(
        entries.length - 1,
        Math.ceil((this.scrollY + this.listH) / ROW_HEIGHT) + 1,
      );

      const time = performance.now() / 1000;
      for (let i = firstVisible; i <= lastVisible; i += 1) {
        const entry = entries[i]!;
        const rowY = this.listTop + i * ROW_HEIGHT - this.scrollY + ROW_HEIGHT / 2;
        const rowRect = {
          x: this.cardX + 8,
          y: rowY - ROW_HEIGHT / 2 + 1,
          w: this.cardW - 16,
          h: ROW_HEIGHT - 2,
        };
        const isMe = entry.name === myName;
        const ov = isMe ? this.myCustomOverrides() : NO_OVERRIDES;
        drawRowBackground(ctx, entry, rowRect.x, rowRect.y, rowRect.w, rowRect.h, time, ov);
        if (isMe) {
          ctx.fillStyle = "rgba(229,57,53,0.18)";
          fillRoundedRect(ctx, rowRect.x, rowRect.y, rowRect.w, rowRect.h, 6);
        }
        drawRowFrame(ctx, entry.equipped_frame ?? null, rowRect, time, ov.frame);
        drawCharacterChip(
          ctx,
          entry.equipped_character ?? null,
          entry.equipped_hat ?? null,
          this.cardX + 36,
          rowY,
          ov.char,
          ov.hat,
        );
        drawRowTrail(ctx, entry.equipped_trail ?? null, this.cardX + 62, rowY, ov.trail);
        // Text rides over the banner→day→night gradient, so it's drawn white
        // with a dark halo to stay readable on both the bright and dark ends.
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.75)";
        ctx.shadowBlur = 3;
        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "left";
        ctx.fillText(`${i + 1}.`, this.cardX + 12, rowY + 4);
        const nameText = truncate(entry.name, MAX_NAME_LENGTH);
        ctx.fillText(nameText, this.cardX + 74, rowY + 4);
        // Clan tag after the name — dimmer + smaller, clipped before the score column.
        if (entry.clan) {
          const nameFont = ctx.font;
          const clanX = this.cardX + 74 + ctx.measureText(nameText).width + 8;
          const maxRight = this.cardX + this.cardW - 86;
          if (clanX < maxRight) {
            ctx.font = "10px ui-monospace, monospace";
            ctx.fillStyle = "#f0d98a";
            ctx.fillText(truncateToWidth(ctx, entry.clan, maxRight - clanX), clanX, rowY + 4);
            ctx.font = nameFont;
            ctx.fillStyle = "#ffffff";
          }
        }
        ctx.textAlign = "right";
        ctx.fillText(String(entry.score), this.cardX + this.cardW - 80, rowY + 4);
        const isCreator = entry.name.toLowerCase() === "daniel guzev";
        if (isCreator) {
          ctx.font = "bold 10px ui-monospace, monospace";
          ctx.fillStyle = "#ffd700";
          ctx.fillText("CREATOR", this.cardX + this.cardW - 16, rowY + 4);
        } else {
          ctx.fillText(String(entry.flies), this.cardX + this.cardW - 24, rowY + 4);
        }
        ctx.restore();
      }
      ctx.restore();

      // scroll indicator
      if (this.maxScroll > 0) {
        const trackX = this.cardX + this.cardW - 6;
        const trackH = this.listH;
        const thumbH = Math.max(20, (this.listH / (this.maxScroll + this.listH)) * trackH);
        const thumbY = this.listTop + (this.scrollY / this.maxScroll) * (trackH - thumbH);
        ctx.fillStyle = "rgba(122,94,42,0.3)";
        fillRoundedRect(ctx, trackX, this.listTop, 3, trackH, 1.5);
        ctx.fillStyle = "#7a5e2a";
        fillRoundedRect(ctx, trackX, thumbY, 3, thumbH, 1.5);
      }
    }

    ctx.fillStyle = "#f5e9c8";
    ctx.textAlign = "center";
    ctx.font = "12px system-ui, sans-serif";
    const hint = this.maxScroll > 0
      ? "drag to scroll · press back button to return"
      : "press back button to return";
    ctx.fillText(hint, VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT - 20);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function drawCharacterChip(
  ctx: CanvasRenderingContext2D,
  charId: string | null,
  hatId: string | null,
  cx: number,
  cy: number,
  charOverride: CharacterRender | null = null,
  hatOverride: HatRender | null = null,
): void {
  let charRender = charOverride;
  if (!charRender) {
    const item = getItem(charId ?? "char.emerald");
    if (!item || item.slot !== "character") return;
    charRender = item.render;
  }
  let hatRender = hatOverride;
  if (!hatRender) {
    const hatItem = hatId ? getItem(hatId) : null;
    hatRender = hatItem && hatItem.slot === "hat" ? hatItem.render : null;
  }
  const item = { render: charRender };
  ctx.save();
  ctx.beginPath();
  ctx.rect(cx - 14, cy - 11, 28, 22);
  ctx.clip();
  ctx.translate(cx, cy);
  ctx.scale(0.42, 0.42);
  const anim = { propAngle: 0.6, bobTime: 0.4, flapTime: 0 };
  if (hatRender) drawHatBack(ctx, 14, hatRender, anim);
  drawCharacterSprite(ctx, 14, item.render, { bobTime: 0.4, flapTime: 0 });
  if (hatRender) drawHatFront(ctx, 14, hatRender, anim);
  ctx.restore();
}

function drawRowTrail(
  ctx: CanvasRenderingContext2D,
  trailId: string | null,
  cx: number,
  cy: number,
  override: TrailRender | null = null,
): void {
  let render = override;
  if (!render) {
    if (!trailId) return;
    const item = getItem(trailId);
    if (!item || item.slot !== "trail") return;
    render = item.render;
  }
  ctx.save();
  ctx.beginPath();
  ctx.rect(cx - 7, cy - 8, 14, 16);
  ctx.clip();
  for (let i = 0; i < 3; i += 1) {
    drawTrailIcon(ctx, render, cx + i * 4 - 2, cy + (i % 2 === 0 ? -1 : 1), 1 - i * 0.25);
  }
  ctx.globalAlpha = 1;
  ctx.restore();
}

function drawRowFrame(
  ctx: CanvasRenderingContext2D,
  frameId: string | null,
  rect: { x: number; y: number; w: number; h: number },
  time: number,
  override: FrameRender | null = null,
): void {
  let render = override;
  if (!render) {
    if (!frameId) return;
    const item = getItem(frameId);
    if (!item || item.slot !== "frame") return;
    render = item.render;
  }
  drawFrameRow(ctx, render, { ...rect, radius: 6 }, time);
}

/**
 * Row background: the player's banner on the left, fading through their day sky
 * into their night sky on the right. Players without equipped skies fall back to
 * the default day/night colors, so every row gets the gradient.
 */
function drawRowBackground(
  ctx: CanvasRenderingContext2D,
  entry: LeaderboardEntry,
  x: number,
  y: number,
  w: number,
  h: number,
  time: number,
  ov: CustomOverrides = NO_OVERRIDES,
): void {
  ctx.save();
  ctx.beginPath();
  roundedRectPath(ctx, x, y, w, h, 6);
  ctx.clip();

  let bannerRender: BannerRender | null = ov.banner;
  if (!bannerRender) {
    const banner = entry.equipped_banner ? getItem(entry.equipped_banner) : null;
    bannerRender = banner && banner.slot === "banner" ? banner.render : null;
  }
  const hasBanner = !!bannerRender;
  if (bannerRender) drawBannerBg(ctx, bannerRender, x, y, w, h, time);

  // A custom scenery has no real sky palette — tint the row by its drawing's
  // dominant color (same hue for both the day and night halves).
  const dayCol = ov.bg
    ? dominantColor(ov.bg.cells ?? [])
    : skyColor(entry.equipped_background ?? null, false);
  const nightCol = ov.bg
    ? darkenHex(dominantColor(ov.bg.cells ?? []), 0.4)
    : skyColor(entry.equipped_background_night ?? null, true);
  const grad = ctx.createLinearGradient(x, y, x + w, y);
  if (hasBanner) {
    // keep the banner visible on the left, then resolve into day, then night
    grad.addColorStop(0, withAlpha(dayCol, 0));
    grad.addColorStop(0.4, withAlpha(dayCol, 0));
    grad.addColorStop(0.6, dayCol);
    grad.addColorStop(0.72, dayCol);
    grad.addColorStop(1, nightCol);
  } else {
    grad.addColorStop(0, dayCol);
    grad.addColorStop(0.55, dayCol);
    grad.addColorStop(1, nightCol);
  }
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, w, h);

  // treeline silhouettes along the bottom — day-colored trees over the day half,
  // night-colored trees over the night half, mirroring the real scenery.
  const dayTree = ov.bg
    ? darkenHex(dominantColor(ov.bg.cells ?? []), 0.6)
    : treeColor(entry.equipped_background ?? null, false);
  const nightTree = ov.bg
    ? darkenHex(dominantColor(ov.bg.cells ?? []), 0.75)
    : treeColor(entry.equipped_background_night ?? null, true);
  const baseY = y + h;
  const treeStart = hasBanner ? x + w * 0.52 : x;
  const nightX = x + w * 0.82;
  const tw = 7;
  let i = 0;
  for (let tx = treeStart; tx < x + w; tx += tw, i += 1) {
    ctx.fillStyle = tx + tw / 2 >= nightX ? nightTree : dayTree;
    const peak = h * (0.34 + (i % 3) * 0.06);
    ctx.beginPath();
    ctx.moveTo(tx, baseY);
    ctx.lineTo(tx + tw / 2, baseY - peak);
    ctx.lineTo(tx + tw, baseY);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

/** Representative color for a player's equipped sky (rainbow skies animate). */
function skyColor(bgId: string | null, night: boolean): string {
  if (bgId) {
    const item = getItem(bgId);
    if (item && (item.slot === "background" || item.slot === "background_night")) {
      if (item.render.rainbow) {
        const t = performance.now() / 1000;
        const hue = (((t * 40 + (night ? 180 : 0)) % 360) + 360) % 360;
        return `hsl(${hue}, 85%, ${night ? 40 : 60}%)`;
      }
      return item.render.skyTop;
    }
  }
  return night ? NIGHT_PALETTE.skyTop : DAY_PALETTE.skyTop;
}

/** Near-tree silhouette color for a player's equipped sky (rainbow skies animate). */
function treeColor(bgId: string | null, night: boolean): string {
  if (bgId) {
    const item = getItem(bgId);
    if (item && (item.slot === "background" || item.slot === "background_night")) {
      if (item.render.rainbow) {
        const t = performance.now() / 1000;
        const hue = (((t * 40 + 250 + (night ? 180 : 0)) % 360) + 360) % 360;
        return `hsl(${hue}, 70%, ${night ? 10 : 20}%)`;
      }
      return item.render.treeNear;
    }
  }
  return night ? NIGHT_PALETTE.treeNear : DAY_PALETTE.treeNear;
}

/** Darken a "#rgb"/"#rrggbb" color toward black by t (0..1). */
function darkenHex(color: string, t: number): string {
  if (!color.startsWith("#")) return color;
  const hex =
    color.length === 4
      ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
      : color;
  const r = Math.round(parseInt(hex.slice(1, 3), 16) * (1 - t));
  const g = Math.round(parseInt(hex.slice(3, 5), 16) * (1 - t));
  const b = Math.round(parseInt(hex.slice(5, 7), 16) * (1 - t));
  return `rgb(${r},${g},${b})`;
}

function withAlpha(color: string, a: number): string {
  if (color.startsWith("#")) {
    const hex =
      color.length === 4
        ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
        : color;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  if (color.startsWith("hsl(")) return color.replace("hsl(", "hsla(").replace(")", `,${a})`);
  if (color.startsWith("rgb(")) return color.replace("rgb(", "rgba(").replace(")", `,${a})`);
  return color;
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
